import { CollapseInfo, SpinResponse, ReelWinPosition } from '@cgs/data-access';
import {
  CollapseAnimationComponent,
  ComponentNames,
  ICollapseAnimationComponentParams,
  PlaySoundAction,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  StopSoundAction,
  TimeScaleComponent,
  TIME_SCALE_COMPONENT_TOKEN,
} from '@cgs/machines';
import { adjustDurationForTimeScale } from '../../helpers/time-scale.helper';
import { NodeUtils } from '@cgs/shared';
import {
  Container,
  easeInOutCubic,
  easeInQuart,
  easeOutCubicInterpolate,
  EmptyAction,
  FunctionAction,
  InterpolateCopyAction,
  InterpolateInplaceAction,
  IntervalAction,
  ParallelAction,
  SceneObject,
  SequenceAction,
  TtfTextSceneObject,
  Vector2,
} from '@cgs/wge';
import {
  COLLAPSE_WIN_COMPONENT_TOKEN,
  CollapseWinComponent,
} from '../collapse-win/collapse-win.component';
import {
  SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN,
  SantaFreeSpinsTotalWinComponent,
} from '../free-spins-total-win/santa-free-spins-total-win.component';
import {
  SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN,
  SantaMultiplierHolderComponent,
} from '../multiplier-holder/santa-multiplier-holder.component';
import {
  SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN,
  SantaTopMultiplierComponent,
} from '../multiplier/santa-top-multiplier.component';

const enum SceneNames {
  WildMultiplierHolder = 'wildMultHolder',
  FlyHolder = 'flyHolder',
  AnimIconsHolder = 'anim_icons_holder',
  IconTxt = 'icon_txt',
  WildIcon = 'icon_20',
}

const enum SceneStates {
  Anim = 'anim',
  Default = 'default',
  Jump0 = 'jump0',
  Jump1 = 'jump1',
}

const enum CollapseWinTypes {
  Stack = 'Stack',
  WildStack = 'WildStack',
}

export class SantaCollapseAnimationComponent extends CollapseAnimationComponent {
  private readonly resourcesComponent: ResourcesComponent;
  private readonly topMultiplierComponent: SantaTopMultiplierComponent;
  private readonly collapseWinComponent: CollapseWinComponent;
  private readonly multiplierHolderComponent: SantaMultiplierHolderComponent;
  private readonly freeSpinsTotalWinComponent: SantaFreeSpinsTotalWinComponent;
  private readonly animaIconHolder: SceneObject;
  private readonly animIconsHolderOriginalZ: number;
  private readonly timeScaleComponent: TimeScaleComponent;

  private readonly flyHolder: SceneObject;

  constructor(container: Container, params: ICollapseAnimationComponentParams) {
    super(container, params);
    this.resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.topMultiplierComponent = container.resolve<SantaTopMultiplierComponent>(
      SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN
    );
    this.collapseWinComponent = container.resolve<CollapseWinComponent>(
      COLLAPSE_WIN_COMPONENT_TOKEN
    );
    this.multiplierHolderComponent = container.resolve<SantaMultiplierHolderComponent>(
      SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN
    );
    this.freeSpinsTotalWinComponent = container.resolve<SantaFreeSpinsTotalWinComponent>(
      SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN
    );
    this.animaIconHolder = this.resourcesComponent.root.findById('anim_icons_holder')!.parent!;

    this.animIconsHolderOriginalZ = this.animaIconHolder.z;
    this.timeScaleComponent = container.resolve<TimeScaleComponent>(TIME_SCALE_COMPONENT_TOKEN);

    this.flyHolder = this.resourcesComponent.root.findById(SceneNames.FlyHolder)!;
  }

  private adjustDuration(duration: number): number {
    return adjustDurationForTimeScale(this.timeScaleComponent, duration);
  }

  protected override bangAction(response: SpinResponse): IntervalAction {
    const bangActions: IntervalAction[] = [];
    const delayActions: IntervalAction[] = [];
    const hideActions: IntervalAction[] = [];
    const flyActions: IntervalAction[] = [new EmptyAction()];

    delayActions.push(this.playBangSoundAction());
    let positions = this.flattenWinPositions(response.currentWinPositions);
    const jumpToWildPositions = this.getJumpToWildPositions(response);

    const jumpWildToPositions = this.getJumpWildToPositions(response.collapseInfo!);

    for (const jumpWildPosition of jumpToWildPositions) {
      flyActions.push(this.flyStackWildToWild(jumpWildPosition));
    }

    if (jumpWildToPositions.length > 0) {
      positions = positions.filter((position) =>
        jumpWildToPositions.every((wildPosition) => wildPosition[0] !== position)
      );
    }
    const collapseSound = this.reelSoundModel.getSoundByName('collapse');
    if (collapseSound) {
      delayActions.push(
        new SequenceAction([new StopSoundAction(collapseSound), new PlaySoundAction(collapseSound)])
      );
    }

    const bangSound = this.reelSoundModel.getSoundByName('general_win');
    if (bangSound) {
      bangActions.push(
        new SequenceAction([new StopSoundAction(bangSound), new PlaySoundAction(bangSound)])
      );
    }

    for (const position of positions) {
      this.createExplodeAnimationWithWinAction(
        bangActions,
        position,
        delayActions,
        hideActions,
        response
      );
    }

    const duration = this.iconAnimationHelper.getMaxAnimDuration(0, SceneStates.Anim);

    bangActions.push(
      new SequenceAction([
        new FunctionAction(() => {
          jumpWildToPositions.forEach(([position]) => {
            this.iconAnimationHelper.startAnimOnIcon(position, SceneStates.Anim);
          });
        }),
        new EmptyAction().withDuration(duration),
        this.freeSpinsTotalWinComponent.showFreeSpinsTotalWinAction(
          response.collapseInfo?.currentRound?.roundWin || 0
        ),
      ])
    );

    hideActions.push(
      new FunctionAction(() => {
        jumpWildToPositions.forEach(([position]) => {
          const line = this.iconAnimationHelper.getLineIndex(position);
          const reel = this.iconAnimationHelper.getReelIndex(position);

          let entities = this.reelsEngine.entityCacheHolder.getAnimationEntities(reel, line, false);
          entities.forEach((e) => this.reelsEngine.hideAnimEntity(e));

          entities = this.reelsEngine.entityCacheHolder.getSoundEntities(reel, line, false);
          entities.forEach((e) => this.reelsEngine.hideSoundEntity(e));

          entities = this.iconAnimationHelper.getEntities(position);
          entities.forEach((e) => {
            this.reelsEngine.entityCacheHolder.removeEntity(reel, line, e);
          });
        });
      })
    );

    return new SequenceAction([
      new ParallelAction(flyActions),
      new ParallelAction(bangActions),
      new ParallelAction(delayActions),
      new ParallelAction(hideActions),
    ]);
  }

  protected createExplodeAnimationWithWinAction(
    bangActions: IntervalAction[],
    position: number,
    delayActions: IntervalAction[],
    hideActions: IntervalAction[],
    response: SpinResponse
  ) {
    this.pushBangWithWinActions(bangActions, position, response);
    this.pushDelayActions(delayActions, position);
    this.pushHideIconsActions(hideActions, position);
  }

  protected pushBangWithWinActions(
    bangActions: IntervalAction[],
    position: number,
    response: SpinResponse
  ): void {
    const actions: IntervalAction[] = [];

    actions.push(
      new FunctionAction(() => {
        this.animaIconHolder.z = this.flyHolder.z + 10;
        this.iconAnimationHelper.startAnimOnIcon(position, SceneStates.Anim);
      })
    );
    actions.push(
      new EmptyAction().withDuration(
        this.iconAnimationHelper.getMaxAnimDuration(position, SceneStates.Anim)
      )
    );
    actions.push(
      new FunctionAction(() => {
        this.animaIconHolder.z = this.animIconsHolderOriginalZ;
        this.iconAnimationHelper.startAnimOnIcon(position, this.params.collapseStateName!);
      }, false)
    );
    actions.push(
      this.collapseWinComponent.showCollapseWinAction(
        response.collapseInfo?.currentRound?.roundWin || 0
      )
    );

    bangActions.push(new SequenceAction(actions));
  }

  protected override flattenWinPositions(winPositions: ReelWinPosition[]): number[] {
    return winPositions
      .filter((winPosition) => winPosition.type === CollapseWinTypes.Stack)
      .reduce((acc, winPosition) => acc.concat(winPosition.positions), [] as number[]);
  }

  protected override collapseWinAction(
    collapseInfo: CollapseInfo,
    bangPositions: number[]
  ): IntervalAction {
    const actions: IntervalAction[] = [];

    const jumpActions = this.jumpWildToPositions(collapseInfo);

    const fallAction = this.fallDownCurrentReels(bangPositions);

    const fallDownAction = new FunctionAction(() =>
      this.fallDownNewReels(
        collapseInfo.currentRound!.positions,
        collapseInfo.currentRound!.newReels
      )
    );

    const fallSound = this.reelSoundModel.getSoundByName('single_spin_stop');

    actions.push(
      new ParallelAction([
        jumpActions,
        new SequenceAction([
          fallAction,
          fallSound
            ? new SequenceAction([new StopSoundAction(fallSound), new PlaySoundAction(fallSound)])
            : new EmptyAction(),
          fallDownAction,
        ]),
      ])
    );
    actions.push(new FunctionAction(() => collapseInfo.nextRound()));
    actions.push(
      new EmptyAction().withDuration(
        this.gameConfig.regularSpinCollapsingConfig.collapsingParameters!.roundFallingStartDelay
      )
    );
    return new SequenceAction(actions);
  }

  private getJumpToWildPositions(response: SpinResponse): number[] {
    const winPositions =
      (response.collapseInfo?.collapsingCounter || 0) > 0
        ? response.collapseInfo?.groups[response.collapseInfo?.collapsingCounter - 1]
            ?.winPositions || []
        : response.winPositions || [];
    return winPositions
      .filter((winPosition) => winPosition.type === CollapseWinTypes.WildStack)
      .reduce((acc, winPosition) => acc.concat(winPosition.positions), [] as number[]);
  }

  private flyStackWildToWild(targetReelPosition: number): IntervalAction {
    const placeHolderWildIcon = this.resourcesComponent.root.findById(
      SceneNames.WildMultiplierHolder
    )!;
    const wildIconScene = this.resourcesComponent.getIcon(SceneNames.WildIcon)[0];

    NodeUtils.removeMask(wildIconScene);

    wildIconScene.position = NodeUtils.getLocalPositionFromPoint(
      placeHolderWildIcon,
      this.flyHolder,
      new Vector2(0, 0)
    );
    this.flyHolder.z = placeHolderWildIcon.z + 1;

    const textScene = wildIconScene.findById<TtfTextSceneObject>(SceneNames.IconTxt)!;
    textScene.text = this.topMultiplierComponent.getMultiplier().toString();
    wildIconScene.scale = placeHolderWildIcon.scale.clone();

    this.flyHolder.addChild(wildIconScene);

    const duration = wildIconScene.stateMachine!.getStateDuration(SceneStates.Jump0);
    const wildFlySound = this.reelSoundModel.getSoundByName('wild_fly0');

    const flyDuration = this.adjustDuration(0.9);
    const jumpDuration = this.adjustDuration(0.6);

    const reelIndex = this.reelsEngine.getReelByPosition(targetReelPosition);
    const lineIndex = this.reelsEngine.getLineByPosition(targetReelPosition);
    const positionIndex = this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position);

    const entity = this.reelsEngine.getReelStoppedEntities(reelIndex, lineIndex)[0];
    const endPosition = entity.get<Vector2>(positionIndex);

    const startPosition = wildIconScene.position.clone().subtract(new Vector2(0, 18));

    const travelDistance = startPosition.distance(endPosition);
    const arcHeight = Math.min(72, Math.max(28, travelDistance * 0.28));
    const swayOffset = Math.min(22, Math.max(6, travelDistance * 0.08));
    const popLift = Math.min(18, Math.max(6, travelDistance * 0.07));
    const popSpan = 0.16; // portion of time spent on quick upward hop
    const directionX = endPosition.x - startPosition.x;
    const normalizedDx = travelDistance > 1 ? directionX / travelDistance : 0;
    const tiltAngle = Math.max(-12, Math.min(12, normalizedDx * 10));

    const interpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
      .withInterpolateFunction((start, end, t, result) => {
        Vector2.lerpInplace(start, end, t, result);
        const arc = Math.sin(Math.PI * t) ** 2; // zero height at ends, smooth slope
        const sway = Math.sin(Math.PI * t) ** 2;
        result.y -= arcHeight * arc;
        result.x += Math.sin(2 * Math.PI * t) * swayOffset * sway;

        const popT = Math.min(1, t / popSpan);
        const popEase = popT * popT * (3 - 2 * popT); // smoothstep
        result.y -= popLift * (1 - popEase);
      })
      .withTimeFunction(easeInOutCubic)
      .withValues(startPosition, endPosition)
      .withDuration(flyDuration);

    const jumpInterpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
      .withInterpolateFunction(Vector2.lerpInplace)
      .withTimeFunction(easeOutCubicInterpolate)
      .withValues(wildIconScene.position.clone(), startPosition)
      .withDuration(jumpDuration);

    const interpolateScale = new InterpolateInplaceAction<Vector2>((v) => v.clone())
      .withInterpolateFunction(Vector2.lerpInplace)
      .withTimeFunction(easeInQuart)
      .withValues(wildIconScene.scale.clone(), new Vector2(1, 1))
      .withDuration(flyDuration);

    const rotationAction = new InterpolateCopyAction<number>()
      .withInterpolateFunction((a, b, t) => a + (b - a) * t)
      .withTimeFunction(easeInOutCubic)
      .withValues(tiltAngle, 0)
      .withDuration(flyDuration);

    interpolateAction.beginning.listen(() => {
      if (wildFlySound) {
        wildFlySound.stop();
        wildFlySound.play();
      }
    });

    interpolateAction.valueChange.listen((position) => {
      wildIconScene.position = position;
    });

    jumpInterpolateAction.valueChange.listen((position) => {
      wildIconScene.position = position;
    });

    interpolateScale.valueChange.listen((scale) => {
      wildIconScene.scale = scale;
    });

    rotationAction.valueChange.listen((rotation) => {
      wildIconScene.rotation = rotation;
    });

    interpolateAction.done.listen(() => {
      wildIconScene.parent?.removeChild(wildIconScene);

      entity.set(this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex), 20);

      const oldIconNode = entity.get<SceneObject>(
        this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.IconNode)
      );

      wildIconScene.position = oldIconNode.position.clone();

      entity.set(
        this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.IconNode),
        wildIconScene
      );

      this.reelsEngine.entityCacheHolder.replaceEntities(reelIndex, lineIndex, [entity]);

      this.multiplierHolderComponent.show();
      wildIconScene.rotation = 0;
    });

    return new ParallelAction([
      new SequenceAction([
        new ParallelAction([
          jumpInterpolateAction,
          new FunctionAction(() => {
            wildIconScene.stateMachine?.switchToState(SceneStates.Jump0);
            this.multiplierHolderComponent.hide();
          }),
          new EmptyAction().withDuration(jumpDuration),
        ]),
        new ParallelAction([
          interpolateAction,
          interpolateScale,
          rotationAction,
          new EmptyAction().withDuration(flyDuration),
        ]),
      ]),
      new EmptyAction().withDuration(duration),
    ]);
  }

  private getJumpWildToPositions(collapseInfo: CollapseInfo): number[][] {
    const winLines = collapseInfo.currentRound?.winLines || [];
    return winLines.map((winLine) => [winLine.positions[0], winLine.positions[1]]);
  }

  private jumpWildToPositions(collapseInfo: CollapseInfo): IntervalAction {
    const positions = this.getJumpWildToPositions(collapseInfo);
    const jumpActions: IntervalAction[] = [];

    for (const position of positions) {
      jumpActions.push(this.flyWildToPosition(position[0], position[1]));
    }

    return new ParallelAction(jumpActions);
  }

  private flyWildToPosition(startPosition: number, endPosition: number): IntervalAction {
    const startReelIndex = this.reelsEngine.getReelByPosition(startPosition);
    const startLineIndex = this.reelsEngine.getLineByPosition(startPosition);

    const endReelIndex = this.reelsEngine.getReelByPosition(endPosition);
    const endLineIndex = this.reelsEngine.getLineByPosition(endPosition);

    const startEntity = this.reelsEngine.getReelStoppedEntities(startReelIndex, startLineIndex)[0];
    const endEntity = this.reelsEngine.getReelStoppedEntities(endReelIndex, endLineIndex)[0];

    const startIconPosition = startEntity.get<Vector2>(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position)
    );
    const endIconPosition = endEntity.get<Vector2>(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position)
    );

    const wildIconScene = this.resourcesComponent.getIcon(SceneNames.WildIcon)[0];
    const wildIconText = wildIconScene.findById<TtfTextSceneObject>(SceneNames.IconTxt)!;
    wildIconText.text = this.topMultiplierComponent.getMultiplier().toString();
    const animIconsHolder = this.resourcesComponent.root.findById(SceneNames.AnimIconsHolder)!;

    const duration = this.adjustDuration(
      wildIconScene.stateMachine!.getStateDuration(SceneStates.Jump1)
    );

    const wildFlySound = this.reelSoundModel.getSoundByName('wild_fly1');

    wildIconScene.position = startIconPosition.clone();

    const travelDistance = startIconPosition.distance(endIconPosition);
    const movingUp = endIconPosition.y < startIconPosition.y;
    const baseArcHeight = Math.min(42, Math.max(10, travelDistance * 0.2));
    const baseSwayOffset = Math.min(16, Math.max(4, travelDistance * 0.06));
    const basePopLift = Math.min(16, Math.max(6, travelDistance * 0.07));
    const arcHeight = movingUp ? baseArcHeight * 0.6 : baseArcHeight;
    const swayOffset = movingUp ? baseSwayOffset * 0.6 : baseSwayOffset;
    const popLift = movingUp ? basePopLift * 0.5 : basePopLift;
    const popSpan = movingUp ? 0.14 : 0.16;
    const directionX = endIconPosition.x - startIconPosition.x;
    const normalizedDx = travelDistance > 1 ? directionX / travelDistance : 0;
    const tiltAngle = Math.max(-10, Math.min(10, normalizedDx * 8));
    const downEase = (r: number, x0: number, dx: number) => x0 + dx * Math.pow(r, 1.35);

    const interpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
      .withInterpolateFunction((start, end, t, result) => {
        Vector2.lerpInplace(start, end, t, result);
        const arc = Math.sin(Math.PI * t) ** 2;
        const sway = Math.sin(Math.PI * t) ** 2;
        result.y -= arcHeight * arc;
        result.x += Math.sin(2 * Math.PI * t) * swayOffset * sway;
        const popT = Math.min(1, t / popSpan);
        const popEase = popT * popT * (3 - 2 * popT);
        result.y -= popLift * (1 - popEase);
      })
      .withTimeFunction(movingUp ? easeInOutCubic : downEase)
      .withValues(startIconPosition.clone(), endIconPosition.clone())
      .withDuration(duration * 0.75);

    const rotationAction = new InterpolateCopyAction<number>()
      .withInterpolateFunction((a, b, t) => a + (b - a) * t)
      .withTimeFunction(easeInOutCubic)
      .withValues(tiltAngle, 0)
      .withDuration(duration * 0.75);

    interpolateAction.beginning.listen(() => {
      animIconsHolder.addChild(wildIconScene);

      if (wildFlySound) {
        wildFlySound.stop();
        wildFlySound.play();
      }

      wildIconScene.stateMachine?.switchToState(SceneStates.Jump1);

      this.reelsEngine.entityCacheHolder.removeEntity(startReelIndex, startLineIndex, startEntity);
      if (this.gameStateMachine.curResponse.freeSpinsInfo) {
        this.topMultiplierComponent.addMultiplier(1);
      }
    });

    interpolateAction.valueChange.listen((position) => {
      wildIconScene.position = position;
    });

    rotationAction.valueChange.listen((rotation) => {
      wildIconScene.rotation = rotation;
    });

    return new SequenceAction([
      new ParallelAction([
        interpolateAction,
        rotationAction,
        new EmptyAction().withDuration(duration),
      ]),
      new FunctionAction(() => {
        wildIconScene.parent?.removeChild(wildIconScene);
        wildIconScene.deinitialize();
        const entity = this.reelsEngine.getStoppedEntities(endReelIndex, endLineIndex)[0];

        entity.set(
          this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex),
          2
        );

        this.reelsEngine.entityCacheHolder.replaceEntities(endReelIndex, endLineIndex, [entity]);
      }),
    ]);
  }
}
